/**
 * Shared browser-family classifier (VEX-7682). Answers "which remedy do we offer?", not
 * "can this browser run the flow?" — that's checkIProovSupport(), and motion is motionApi.
 */
export type BrowserFamily =
  | "facebook"
  | "instagram"
  | "google-app"
  | "duckduckgo-ios"
  | "edge-ios"
  | "firefox-ios"
  | "chrome-ios"
  | "safari-ios"
  | "other";

export interface BrowserClassification {
  family: BrowserFamily;
  /** iOS/iPadOS, including iPadOS reporting a Mac UA under "Request Desktop Website". */
  isIOS: boolean;
  /** An iOS browser that is NOT real Safari — the two-grant WKWebView model. */
  isWKWebViewApp: boolean;
  /** Embedded in a host app rather than standalone. "Open in Safari" applies. */
  isInAppBrowser: boolean;
  /**
   * Whether a host-page reload can re-prompt for a denied permission. True for real Safari
   * (grant is document-load scoped) and non-iOS; false for WKWebView apps (WebKit #220416).
   * Fails open — an unrecognised UA keeps the existing remedy.
   */
  reloadCanRePrompt: boolean;
}

/**
 * FBAN/FBAV are Facebook iOS, FB_IAB its Android in-app browser; GSA is the Google app
 * (iOS only — the Android Google app has no distinguishing token, a known blind spot).
 */
const MARKERS: ReadonlyArray<readonly [BrowserFamily, RegExp, { inApp: boolean }]> = [
  ["facebook", /\b(FBAN|FBAV|FB_IAB)\b/, { inApp: true }],
  ["instagram", /\bInstagram\b/i, { inApp: true }],
  ["google-app", /\bGSA\//, { inApp: true }],
  // Current iOS builds send `Ddg/<ver>`, not `DuckDuckGo/` (VEX-8056).
  ["duckduckgo-ios", /\b(DuckDuckGo|Ddg)\//, { inApp: false }],
  ["edge-ios", /\bEdgiOS\//, { inApp: false }],
  ["firefox-ios", /\bFxiOS\//, { inApp: false }],
  ["chrome-ios", /\bCriOS\//, { inApp: false }],
] as const;

/**
 * The maxTouchPoints clause catches iPadOS in "Request Desktop Website" mode, which reports
 * a Mac UA. Real Macs report 0, Touch Bar included. Mirrors isDesktopModeSpoofed().
 */
function detectIOS(userAgent: string, maxTouchPoints: number): boolean {
  if (/\b(iPhone|iPad|iPod)\b/.test(userAgent)) return true;
  return /Mac ?OS/i.test(userAgent) && maxTouchPoints > 1;
}

/** Pure and synchronous; never throws. An unrecognised UA is `"other"`, reload left enabled. */
export function classifyBrowser(
  userAgent: string = typeof navigator !== "undefined" ? navigator.userAgent : "",
  maxTouchPoints: number = typeof navigator !== "undefined" ? (navigator.maxTouchPoints ?? 0) : 0
): BrowserClassification {
  const ua = userAgent || "";
  const isIOS = detectIOS(ua, maxTouchPoints);

  // Most-specific first: in-app UAs still end in "Safari/604.1" and some carry a Chrome
  // segment, so a Safari- or Chrome-first test misfiles them as their host browser.
  const marker = MARKERS.find(([, pattern]) => pattern.test(ua));

  if (marker) {
    const [family, , meta] = marker;
    return {
      family,
      isIOS,
      // Android in-app browsers are Chromium, so they must not inherit the iOS verdict.
      isWKWebViewApp: isIOS,
      isInAppBrowser: meta.inApp,
      reloadCanRePrompt: !isIOS,
    };
  }

  if (isIOS) {
    // No app marker on iOS means real Safari — the one browser where reload re-prompts.
    return {
      family: "safari-ios",
      isIOS: true,
      isWKWebViewApp: false,
      isInAppBrowser: false,
      reloadCanRePrompt: true,
    };
  }

  return {
    family: "other",
    isIOS: false,
    isWKWebViewApp: false,
    isInAppBrowser: false,
    reloadCanRePrompt: true,
  };
}
